<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePage } from '@inertiajs/vue3'
import UiButton from '@/Components/UI/UiButton.vue'
import UiMenu from '@/Components/UI/UiMenu.vue'
import Icon from '@/Components/UI/Icon.vue'

const props = defineProps({
  conversationId: { type: Number, required: true },
  takenById:      { type: Number, default: null },
  users:          { type: Array, default: () => [] },
})
const emit = defineEmits(['changed'])

const { t } = useI18n()
const page = usePage()
const me = computed(() => page.props.auth?.user)

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || ''
const busy = ref(false)

// Excludes the current claim-holder and the current user (they wouldn't be
// reassigning to themselves) so the menu only shows useful targets.
const others = computed(() =>
  props.users.filter((u) => u.id !== props.takenById && u.id !== me.value?.id),
)

const assign = async (userId) => {
  if (busy.value) return
  busy.value = true
  try {
    const res = await fetch(`/inbox/conversations/${props.conversationId}/assign`, {
      method: 'PATCH',
      headers: {
        'Content-Type':     'application/json',
        'Accept':           'application/json',
        'X-CSRF-Token':     csrfToken(),
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ user_id: userId }),
    })
    const data = await res.json().catch(() => ({}))
    if (res.ok && data.ok) {
      emit('changed', data.conversation)
    } else {
      console.warn('[inbox] assign failed', data.error || res.status)
    }
  } catch (e) {
    console.warn('[inbox] assign network error', e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <UiMenu v-if="others.length">
    <template #trigger="{ toggle }">
      <UiButton
        variant="ghost"
        size="sm"
        :loading="busy"
        :title="t('inbox.thread.reassign')"
        @click="toggle"
      >
        <Icon name="users" :size="14" />
        <span class="ml-1">{{ t('inbox.thread.reassign') }}</span>
      </UiButton>
    </template>
    <div class="px-3 py-1 text-[10px] uppercase tracking-wider text-[color:var(--muted)]">
      {{ t('inbox.thread.reassignTo') }}
    </div>
    <button
      v-for="u in others"
      :key="u.id"
      type="button"
      class="block w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--surfaceAlt)]"
      @click="assign(u.id)"
    >
      <span class="fw-value text-[color:var(--ink)]">{{ u.name }}</span>
      <span class="block text-xs text-[color:var(--muted)] capitalize">{{ u.role }}</span>
    </button>
  </UiMenu>
</template>
